import React, { useRef } from "react";
import { Link } from "react-router-dom";
import type { UseQueryResult } from "@tanstack/react-query";
import { ChevronLeft, ChevronRight, Trophy } from "lucide-react";
import type { CommunityLeaderboardRow } from "lib/api/community";
import type { CommunityUiStrings } from "i18n/communityUi";
import ActorAvatar from "components/Actors/ActorAvatar";
import { ACTOR_AVATAR_SIZES } from "util/actorAvatarSizes";
import { Button } from "ui/Button";
import SectionShell from "../SectionShell";
import {
  SectionEmptyState,
  SectionErrorState,
  SectionLoadingState,
} from "../SectionStates";
import { buildUserHref } from "../utils";

interface TopContributorsProps {
  query: UseQueryResult<CommunityLeaderboardRow[]>;
  labels: CommunityUiStrings;
  numberFormatter: Intl.NumberFormat;
}

/**
 * Horizontal contributor strip with keyboard-accessible scrolling.
 */
const TopContributors: React.FC<TopContributorsProps> = ({
  query,
  labels,
  numberFormatter,
}) => {
  const scrollRef = useRef<HTMLDivElement | null>(null);

  const scrollByCard = (direction: -1 | 1) => {
    const viewport = scrollRef.current;
    if (!viewport) {
      return;
    }

    viewport.scrollBy({
      left: direction * Math.max(viewport.clientWidth * 0.8, 260),
      behavior: "smooth",
    });
  };

  return (
    <SectionShell
      title={labels.contributorsTitle}
      subtitle={labels.contributorsSubtitle}
      actions={
        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="outline"
            size="icon"
            onClick={() => scrollByCard(-1)}
            aria-label={labels.contributorScrollPrevious}
          >
            <ChevronLeft className="size-4" aria-hidden />
          </Button>
          <Button
            type="button"
            variant="outline"
            size="icon"
            onClick={() => scrollByCard(1)}
            aria-label={labels.contributorScrollNext}
          >
            <ChevronRight className="size-4" aria-hidden />
          </Button>
        </div>
      }
      contentClassName="space-y-4"
    >
      {query.isPending ? (
        <SectionLoadingState message={labels.loading} />
      ) : query.isError ? (
        <SectionErrorState
          message={labels.sectionError}
          retryLabel={labels.retry}
          onRetry={() => {
            void query.refetch();
          }}
          isRetrying={query.isRefetching}
        />
      ) : query.data && query.data.length > 0 ? (
        <div
          ref={scrollRef}
          tabIndex={0}
          onKeyDown={(event) => {
            if (event.key === "ArrowLeft") {
              event.preventDefault();
              scrollByCard(-1);
            }
            if (event.key === "ArrowRight") {
              event.preventDefault();
              scrollByCard(1);
            }
          }}
          className="flex snap-x snap-mandatory gap-4 overflow-x-auto pb-2 focus-visible:outline-none"
        >
          {query.data.map((row, index) => {
            const total = Math.max(
              row.comment_count + row.post_count + row.ad_break_count + row.award_receive_count,
              1
            );

            return (
              <article
                key={row.user_id}
                className="min-w-[18rem] snap-start rounded-2xl border border-eoe-border/80 bg-eoe-bg-hover/25 p-4 dark:border-eoe-dark-border/80 dark:bg-eoe-dark-bg-hover/15"
              >
                <div className="space-y-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-center gap-3">
                      <ActorAvatar
                        actor={{
                          id: row.user_id,
                          kind: "user",
                          username: row.username,
                          displayName: null,
                          avatarKey: null,
                        }}
                        size={ACTOR_AVATAR_SIZES.inboxRow}
                      />
                      <div className="space-y-1">
                        <Link
                          to={buildUserHref(row.username)}
                          className="font-medium text-eoe-text hover:text-blue-600 hover:underline dark:text-eoe-dark-text dark:hover:text-blue-400"
                        >
                          {row.username}
                        </Link>
                        <p className="text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub">
                          {labels.activityLayerTotal}: {numberFormatter.format(row.activity_total)}
                        </p>
                      </div>
                    </div>
                    <div className="inline-flex items-center gap-1 rounded-full border border-amber-300/70 bg-amber-50 px-2.5 py-1 text-xs font-medium text-amber-700 dark:border-amber-500/40 dark:bg-amber-950/30 dark:text-amber-200">
                      <Trophy className="size-3.5" aria-hidden />
                      #{index + 1}
                    </div>
                  </div>
                  <div className="space-y-2">
                    <div className="flex h-2 overflow-hidden rounded-full bg-eoe-border/70 dark:bg-eoe-dark-border/70">
                      <div className="bg-blue-600" style={{ width: `${(row.comment_count / total) * 100}%` }} />
                      <div className="bg-teal-500" style={{ width: `${(row.post_count / total) * 100}%` }} />
                      <div className="bg-violet-500" style={{ width: `${(row.ad_break_count / total) * 100}%` }} />
                      <div className="bg-amber-500" style={{ width: `${(row.award_receive_count / total) * 100}%` }} />
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-xs text-eoe-text-sub dark:text-eoe-dark-text-sub">
                      <span>{labels.contributorComments}: {numberFormatter.format(row.comment_count)}</span>
                      <span>{labels.contributorPosts}: {numberFormatter.format(row.post_count)}</span>
                      <span>{labels.contributorAdBreaks}: {numberFormatter.format(row.ad_break_count)}</span>
                      <span>{labels.contributorAwards}: {numberFormatter.format(row.award_receive_count)}</span>
                    </div>
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      ) : (
        <SectionEmptyState title={labels.emptyContributors} />
      )}
    </SectionShell>
  );
};

export default TopContributors;

